import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, CookingPot, Plus, X, FilePdf } from '@phosphor-icons/react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import RecipePickerDialog from '@/components/RecipePickerDialog';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';

const API = process.env.REACT_APP_BACKEND_URL;
const PROGRAM_TYPES = [
  { key: 'gravida', label: 'Super Barriguinhas' },
  { key: 'pos_parto', label: 'Super Mamãs' },
  { key: 'pre_concecao', label: 'Super Pré Mamãs' },
  { key: 'introducao_alimentar', label: 'Super Bebés' },
  { key: 'emagrecimento', label: 'Emagrecimento' },
  { key: 'geral', label: 'Geral' },
];
const DAYS = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
const DAY_LABELS = { segunda: 'Segunda', terca: 'Terça', quarta: 'Quarta', quinta: 'Quinta', sexta: 'Sexta', sabado: 'Sábado', domingo: 'Domingo' };
const MEAL_TYPES = ['sopa_almoco', 'almoco', 'sopa_jantar', 'jantar'];
const MEAL_LABELS = { sopa_almoco: 'Sopa (almoço)', almoco: 'Almoço', sopa_jantar: 'Sopa (jantar)', jantar: 'Jantar' };
const EXTRA_SECTIONS = [
  { key: 'sopas', label: 'Sopas' },
  { key: 'acompanhamentos', label: 'Acompanhamentos' },
  { key: 'snacks', label: 'Snacks' },
  { key: 'jantares_rapidos', label: 'Jantares rápidos' },
];

export default function AdminEmentaFixaPage() {
  useRequireAdmin();
  const [programType, setProgramType] = useState('gravida');
  const [menu, setMenu] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [weekIdx, setWeekIdx] = useState(0);
  const [selectingMeal, setSelectingMeal] = useState(null); // { day, mealType } | 'extra:<section>'
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchMenu = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/program-menus/${programType}`);
      setMenu(data);
      setNote(data.note || '');
      setWeekIdx(0);
    } catch {
      toast.error('Erro ao carregar ementa fixa');
    } finally {
      setLoading(false);
    }
  }, [programType]);

  const fetchRecipes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/recipes`);
      setRecipes(data);
    } catch {}
  }, []);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);
  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);

  const getRecipeName = (id) => id ? (recipes.find(r => r.id === id)?.name || 'Receita') : null;

  const handleSelectRecipe = async (recipeId) => {
    if (!selectingMeal) return;
    try {
      if (typeof selectingMeal === 'object') {
        const { day, mealType } = selectingMeal;
        const { data } = await axios.put(`${API}/api/program-menus/${programType}/meal`, {
          week_number: weekIdx + 1, day, meal_type: mealType, recipe_id: recipeId,
        });
        setMenu(data);
      } else if (typeof selectingMeal === 'string' && selectingMeal.startsWith('extra:')) {
        const section = selectingMeal.split(':')[1];
        const current = menu?.extra_sections?.[section] || [];
        if (current.includes(recipeId)) { toast.error('Já adicionada'); return; }
        const { data } = await axios.put(`${API}/api/program-menus/${programType}/extra`, { section, recipe_ids: [...current, recipeId] });
        setMenu(data);
      }
      toast.success('Guardado');
    } catch {
      toast.error('Erro ao guardar');
    }
    setSelectingMeal(null);
  };

  const handleClearMeal = async (day, mealType) => {
    try {
      const { data } = await axios.put(`${API}/api/program-menus/${programType}/meal`, {
        week_number: weekIdx + 1, day, meal_type: mealType, recipe_id: null,
      });
      setMenu(data);
    } catch { toast.error('Erro ao remover'); }
  };

  const handleRemoveExtra = async (section, recipeId) => {
    try {
      const current = (menu?.extra_sections?.[section] || []).filter(id => id !== recipeId);
      const { data } = await axios.put(`${API}/api/program-menus/${programType}/extra`, { section, recipe_ids: current });
      setMenu(data);
    } catch { toast.error('Erro ao remover'); }
  };

  const saveNote = async () => {
    try {
      const { data } = await axios.put(`${API}/api/program-menus/${programType}/note`, { note });
      setMenu(data);
      toast.success('Nota guardada');
    } catch { toast.error('Erro ao guardar nota'); }
  };

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/api/program-menus/${programType}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `ementa_fixa_${programType}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao gerar PDF'); }
  };

  const week = menu?.weeks?.[weekIdx];

  return (
    <div className="max-w-3xl mx-auto pb-12" data-testid="admin-ementa-fixa-page">
      <h1 className="font-heading font-medium text-2xl text-brand-text mb-1">Ementa Fixa por Programa</h1>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-brand-muted">Ementa de 4 semanas, visível para todas as clientes com esta tag de programa.</p>
        <Button onClick={downloadPdf} variant="outline" className="rounded-full border-brand-border flex-shrink-0 ml-3" data-testid="admin-download-pdf-btn">
          <FilePdf size={16} className="mr-1.5" /> Exportar PDF
        </Button>
      </div>

      {/* Program tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-5" data-testid="program-type-tabs">
        {PROGRAM_TYPES.map(p => (
          <button
            key={p.key}
            onClick={() => setProgramType(p.key)}
            className={`px-3.5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${programType === p.key ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted hover:bg-brand-mint/20'}`}
            data-testid={`program-tab-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-brand-muted" data-testid="ementa-fixa-loading">A carregar...</div>
      ) : (
        <>
          {/* Week pager */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <button disabled={weekIdx === 0} onClick={() => setWeekIdx(w => Math.max(0, w - 1))} className="p-2 rounded-full hover:bg-brand-mint/20 disabled:opacity-30 transition-colors" data-testid="admin-week-prev">
              <CaretLeft size={18} className="text-brand-green" />
            </button>
            <span className="font-heading font-medium text-brand-text" data-testid="admin-week-label">Semana {weekIdx + 1} de 4</span>
            <button disabled={weekIdx === 3} onClick={() => setWeekIdx(w => Math.min(3, w + 1))} className="p-2 rounded-full hover:bg-brand-mint/20 disabled:opacity-30 transition-colors" data-testid="admin-week-next">
              <CaretRight size={18} className="text-brand-green" />
            </button>
          </div>

          {/* Days grid */}
          <div className="space-y-3 mb-6">
            {DAYS.map(day => (
              <div key={day} className="bg-white border border-brand-border rounded-3xl p-4 shadow-[0_8px_32px_rgba(101,113,97,0.04)]" data-testid={`admin-day-${day}`}>
                <h3 className="font-heading font-medium text-brand-text mb-3">{DAY_LABELS[day]}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {MEAL_TYPES.map(mealType => {
                    const recipeId = week?.days?.[day]?.[mealType];
                    const recipeName = getRecipeName(recipeId);
                    return (
                      <div key={mealType}>
                        <p className="text-xs text-brand-muted mb-1.5 uppercase tracking-wider">{MEAL_LABELS[mealType]}</p>
                        {recipeName ? (
                          <div className="group relative">
                            <button
                              onClick={() => setSelectingMeal({ day, mealType })}
                              className="w-full text-left bg-brand-mint/15 border border-brand-mint/30 rounded-2xl px-3 py-2.5 text-sm text-brand-text transition-all hover:border-brand-green/40"
                              data-testid={`admin-meal-${day}-${mealType}`}
                            >
                              <CookingPot size={14} className="inline mr-1.5 text-brand-green" weight="duotone" />
                              {recipeName}
                            </button>
                            <button
                              onClick={() => handleClearMeal(day, mealType)}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-100 text-red-500 rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`admin-clear-${day}-${mealType}`}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setSelectingMeal({ day, mealType })}
                            className="w-full border-2 border-dashed border-brand-border rounded-2xl px-3 py-2.5 text-sm text-brand-placeholder hover:border-brand-green/40 hover:text-brand-muted transition-all flex items-center gap-1.5"
                            data-testid={`admin-add-meal-${day}-${mealType}`}
                          >
                            <Plus size={14} /> Escolher
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Extra reusable sections */}
          <div className="space-y-4 mb-6">
            {EXTRA_SECTIONS.map(section => (
              <div key={section.key} className="bg-white border border-brand-border rounded-3xl p-4" data-testid={`admin-extra-${section.key}`}>
                <h4 className="font-heading font-medium text-sm text-brand-text mb-2.5">{section.label}</h4>
                <div className="flex flex-wrap gap-2">
                  {(menu?.extra_sections?.[section.key] || []).map(id => (
                    <span key={id} className="flex items-center gap-1.5 bg-brand-mint/15 border border-brand-mint/30 text-sm text-brand-text px-3 py-1.5 rounded-full" data-testid={`admin-extra-chip-${id}`}>
                      {getRecipeName(id)}
                      <button onClick={() => handleRemoveExtra(section.key, id)} data-testid={`admin-extra-remove-${id}`}><X size={12} className="text-brand-muted hover:text-red-500" /></button>
                    </span>
                  ))}
                  <button
                    onClick={() => setSelectingMeal(`extra:${section.key}`)}
                    className="flex items-center gap-1 text-sm text-brand-green hover:text-brand-green-hover px-3 py-1.5 rounded-full border-2 border-dashed border-brand-border hover:border-brand-green/40 transition-all"
                    data-testid={`admin-extra-add-${section.key}`}
                  >
                    <Plus size={14} /> Adicionar
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Fixed note */}
          <div className="bg-white border border-brand-border rounded-3xl p-4">
            <h4 className="font-heading font-medium text-sm text-brand-text mb-2.5">Nota informativa fixa</h4>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Texto visível às clientes deste programa, ex.: dicas gerais, avisos, orientações..."
              className="rounded-2xl border-brand-border min-h-[100px] mb-3"
              data-testid="admin-program-note-input"
            />
            <Button onClick={saveNote} className="rounded-full bg-brand-green hover:bg-brand-green-hover" data-testid="admin-save-note-btn">Guardar nota</Button>
          </div>
        </>
      )}

      <RecipePickerDialog
        open={!!selectingMeal}
        onClose={() => setSelectingMeal(null)}
        recipes={recipes}
        onSelect={handleSelectRecipe}
        title={typeof selectingMeal === 'object' && selectingMeal ? `${DAY_LABELS[selectingMeal.day]} ${MEAL_LABELS[selectingMeal.mealType]}` : ''}
      />
    </div>
  );
}
